import { Component, Input, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OrganizationFile } from '../../../../../models/entities/organization-file';
import { TreeModel, TreeNode } from '@circlon/angular-tree-component';
import { DocumentsService } from '../../../../../services/communication/documents.service';

@UntilDestroy()
@Component({
    selector: 'app-document-list',
    templateUrl: './document-list.component.html',
    styleUrls: ['./document-list.component.scss'],
})
export class DocumentListComponent implements OnInit
{
    @Input() excludedDocuments: OrganizationFile[] = [];
    @Input() orgId = null;

    nodes = [];
    filteredNodes = [];
    options = {
        allowDrag: true,
        useCheckbox: true,
        useTriState: true,
    };
    isDataLoading = true;
    orgDocuments: OrganizationFile[] = [];
    hasRowsSelected = false;

    constructor(
            private _documentsService: DocumentsService,
    ) {
    }

    ngOnInit(): void {
        this.getFlatDocuments();
    }

    getFlatDocuments() {
        this._documentsService
                .getFlatDocumentsForOrganization(this.orgId)
                .pipe(untilDestroyed(this))
                .subscribe((res: Object[]) => {
                    const files = res.map((doc: any) => {
                        const file = new OrganizationFile(doc);
                        if (!file.directory) {
                            if (this.search(['.doc'], file.fileName)) {
                                file['icon'] = 'doc';
                            }
                            if (this.search([
                                '.xls',
                                '.csv',
                            ], file.fileName)) {
                                file['icon'] = 'xls';
                            }
                            if (this.search(['.pdf'], file.fileName)) {
                                file['icon'] = 'pdf';
                            }
                            if (
                                    this.search(
                                            [
                                                '.png',
                                                '.jpg',
                                                '.jpeg',
                                            ],
                                            file.fileName,
                                    )
                            ) {
                                file['icon'] = 'png';
                            }
                            if (
                                    !this.search(
                                            [
                                                '.png',
                                                '.jpg',
                                                '.jpeg',
                                                '.pdf',
                                                '.xls',
                                                '.csv',
                                                '.doc',
                                            ],
                                            file.fileName,
                                    )
                            ) {
                                file['icon'] = 'pdf';
                            }
                        } else {
                            file['icon'] = 'folder';
                        }
                        return file;
                    });
                    this.orgDocuments = files;
                    this.nodes = this.createTree(this.orgDocuments);
                    this.filteredNodes = this.nodes;
                    this.isDataLoading = false;
                });
    }

    filterDocuments(search: string) {
        if (search) {
            this.filteredNodes = this.nodes.filter((file: OrganizationFile) => {
                return this.fileSearch(file, search);
            });
        } else {
            this.filteredNodes = this.createTree(this.orgDocuments);
        }
    }

    fileSearch(file: OrganizationFile, searchValue: string): boolean {
        const searchString = searchValue.toLocaleLowerCase();
        const searchTarget = file.fileName + file.icon;
        if (searchTarget.toLocaleLowerCase().indexOf(searchString.trim()) === -1) {
            return false;
        }

        return true;
    }

    checkFile(file: any, checked: boolean): void {
        // loop through and check our flat documents (orgDocuments)
        this.orgDocuments = this.orgDocuments.map(doc => {
            if (file.id === doc.id) {
                doc.checked = checked;
            }
            return doc;
        });

        file.checked = checked;

        if (checked) {
            this.hasRowsSelected = true;
        } else {
            const files = this.getCheckedDocuments();

            this.hasRowsSelected = !!files.length;
        }
    }

    filterFn(value: string, treeModel: TreeModel) {
        treeModel.filterNodes((node: TreeNode) =>
                this.fuzzySearch(value, node.data.name),
        );
    }

    fuzzySearch(needle: string, haystack: string) {
        const haystackLC = haystack.toLowerCase();
        const needleLC = needle.toLowerCase();

        const hlen = haystack.length;
        const nlen = needleLC.length;

        if (nlen > hlen) {
            return false;
        }
        if (nlen === hlen) {
            return needleLC === haystackLC;
        }
        outer: for (let i = 0, j = 0; i < nlen; i++) {
            const nch = needleLC.charCodeAt(i);

            while (j < hlen) {
                if (haystackLC.charCodeAt(j++) === nch) {
                    continue outer;
                }
            }
            return false;
        }
        return true;
    }

    // noinspection JSMethodCanBeStatic
    search(needles: string[], haystack: string) {
        for (const needle of needles) {
            if (haystack.search(needle) > 0) {
                return true;
            }
        }
        return false;
    }

    createTree(data) {
        const hashTable = Object.create(null);
        data.forEach(aData => {
            hashTable[aData.id] = {
                ...aData,
                children: [],
                name: aData.fileName,
            };
        });
        const dataTree = [];
        data.forEach(aData => {
            const isExcluded = this.excludedDocuments.filter(document => {
                if (aData.id === document.id) {
                    return document;
                }
            })[0];
            if (!isExcluded) {
                if (aData.parentId) {
                    hashTable[aData.parentId].children.push(
                            hashTable[aData.id],
                    );
                } else {
                    dataTree.push(hashTable[aData.id]);
                }
            }
        });
        return dataTree;
    }

    getCheckedDocuments(): OrganizationFile[] {
        const files: OrganizationFile[] = [];
        for (const file of this.orgDocuments) {
            //noinspection TypeScriptUnresolvedVariable
            if (file.checked) {
                files.push(file);
            }
        }

        return files;
    }

}
